// Event-dashboard data layer. Ported from the .136 source, rewired for
// teliance-events: every call is tenant-scoped (getApiBase() →
// /api/tenants/<slug>/event-dashboard) and authenticated through the SPA's
// apiFetch (Bearer token + single-flight 401 refresh). Response envelopes,
// retry/timeout behavior, and the GraphQL queries are unchanged.

import { apiFetch } from '@/lib/api.js';
import { HOST_ID } from './constants';
import { getApiBase } from './dashboardContext';
import { UPCOMING_EVENTS_QUERY, EVENT_DETAIL_QUERY } from './queries';

// apiFetch + AbortController timeout. apiFetch passes options (incl. signal)
// straight to fetch, so the abort still works and we still get auth/refresh.
const fetchWithTimeout = (url, options = {}, timeoutMs = 10000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return apiFetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

const withRetry = async (fn, { retries = 2, baseDelay = 400 } = {}) => {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try { return await fn(attempt); } catch (err) {
      lastErr = err;
      if (attempt === retries) break;
      await new Promise((r) => setTimeout(r, baseDelay * Math.pow(2, attempt)));
    }
  }
  throw lastErr;
};

// timeout 30s: the Universe list query genuinely takes ~19s server-side (the
// server's own upstream timeout is 25s) — a 12s client timeout could NEVER
// succeed on a cache miss, which is exactly how refreshes sat blank.
// Live door occupancy (security check-in on .136, proxied + cached server-side).
// Live door occupancy for a specific event (server composes Tallyfi + Universe
// scan-ins + .230 tables, 15s cached per event). Pass the card's own event so
// the server can resolve slug/date without re-looking-up.
export const fetchOccupancy = async (event) => {
  // Per-tier sold from the card's own Universe detail (tierSales, keyed by
  // rate name — the same names the check-ins report uses). The server merges
  // these with per-tier scan counts for the Tickets chevron breakdown.
  const tiers = event.tierSales
    ? Object.entries(event.tierSales).map(([name, t]) => ({ name, sold: t?.sold || 0 }))
    : undefined;
  const response = await fetchWithTimeout(`${getApiBase()}/occupancy`, {
    method: 'POST',
    body: JSON.stringify({ event: { id: event.id, slug: event.slug, date: event.date, sold: event.sold, title: event.title, tiers } }),
  }, 12000);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const fetchUpcomingEvents = async (limit = 300, { timeout = 30000, retries = 1 } = {}) => {
  return withRetry(async () => {
    const response = await fetchWithTimeout(`${getApiBase()}/graphql/list`, {
      method: 'POST',
      body: JSON.stringify({ query: UPCOMING_EVENTS_QUERY, variables: { hostId: HOST_ID, limit } }),
    }, timeout);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.errors) throw new Error(`GraphQL error: ${JSON.stringify(data.errors)}`);
    const events = data.data?.host?.events?.nodes || [];
    return {
      events,
      updatedAt: data.updatedAt || data._fetchedAt,
      cacheStatus: data._cacheStatus ? {
        status: data._cacheStatus,
        timestamp: data._cachedAt || data._fetchedAt,
        warning: data._staleWarning,
      } : null,
    };
  }, { retries });
};

export const fetchEventDetail = async (eventId, timeslotId, forceRefresh = false, { timeout = 12000, retries = 2 } = {}) => {
  return withRetry(async () => {
    const url = new URL(`${getApiBase()}/graphql/detail`, window.location.origin);
    if (forceRefresh) url.searchParams.append('force', 'true');
    url.searchParams.append('maxAge', '600'); // viewing a >10min-old detail triggers a bg revalidate server-side
    const response = await fetchWithTimeout(url.toString(), {
      method: 'POST',
      body: JSON.stringify({ query: EVENT_DETAIL_QUERY, variables: { id: eventId, timeSlotIds: [timeslotId] } }),
    }, timeout);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.errors) throw new Error(`GraphQL error: ${JSON.stringify(data.errors)}`);
    const event = data.data?.event;
    if (event && data.updatedAt) event._updatedAt = data.updatedAt;
    return event;
  }, { retries });
};

export const fetchEventDetailsBatch = async (events, { timeout = 20000, retries = 1 } = {}) => {
  if (!events || events.length === 0) return { details: {}, errors: {} };
  return withRetry(async () => {
    const body = {
      events: events.map(({ eventId, timeslotId }) => ({
        query: EVENT_DETAIL_QUERY, variables: { id: eventId, timeSlotIds: [timeslotId] },
      })),
    };
    const response = await fetchWithTimeout(`${getApiBase()}/graphql/details`, {
      method: 'POST', body: JSON.stringify(body),
    }, timeout);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    const details = {};
    Object.entries(data.details || {}).forEach(([eventId, payload]) => {
      const event = payload?.data?.event;
      if (event) { if (payload.updatedAt) event._updatedAt = payload.updatedAt; details[eventId] = event; }
    });
    return { details, errors: data.errors || {}, updatedAt: data.updatedAt };
  }, { retries });
};

export const fetchMetaCampaigns = async ({ timeout = 12000, retries = 2 } = {}) => {
  return withRetry(async () => {
    const response = await fetchWithTimeout(`${getApiBase()}/meta/campaigns`, { method: 'POST' }, timeout);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to fetch Meta campaigns');
    return data;
  }, { retries });
};

// Past events: server-side paginated/sorted/searched, header-only (no
// detail_data). Returns { data, total }. limit=0 ⇒ count-only (no rows) for
// the mount-time tab count. Detail for one row comes from fetchPastEventDetail.
export const fetchPastEvents = async ({ limit = 50, offset = 0, q = '', sort = 'date', dir = 'desc', timeout = 15000, retries = 1 } = {}) => {
  return withRetry(async () => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset), sort, dir });
    if (q) params.set('q', q);
    const response = await fetchWithTimeout(`${getApiBase()}/past-events?${params.toString()}`, {}, timeout);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to fetch past events');
    return { data: data.data || [], total: data.total || 0, occupancySupported: !!data.occupancy_supported };
  }, { retries });
};

// Full detail (detail_data) for a single archived event — fast PG PK lookup,
// fetched on expand.
export const fetchPastEventDetail = async (eventId, { timeout = 12000, retries = 2 } = {}) => {
  return withRetry(async () => {
    const response = await fetchWithTimeout(`${getApiBase()}/past-events/${encodeURIComponent(eventId)}`, {}, timeout);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to fetch past event detail');
    return data.data;
  }, { retries });
};

export const fetchSystemHealth = async () => {
  try {
    const base = getApiBase();
    const healthResponse = await apiFetch(`${base}/health`);
    const healthData = healthResponse.ok ? await healthResponse.json() : null;
    let cacheData = null;
    try {
      const cacheResponse = await apiFetch(`${base}/cache/status`);
      if (cacheResponse.ok) cacheData = await cacheResponse.json();
    } catch { /* cache status optional */ }
    return { healthData, cacheData };
  } catch (err) {
    if (!err.message?.includes('Failed to fetch')) console.error('Error fetching system health:', err);
    throw err;
  }
};
